/**
 * Piano Lab Article — RTINGS-style comparison article
 * Handles: scroll spy, sticky header, expand/collapse, sidebar navigation, mobile drawer
 */

if (!customElements.get('piano-lab-article')) {
  class PianoLabArticle extends HTMLElement {
    constructor() {
      super();
      this.sections = [];
      this.navLinks = [];
      this.stickyHeader = null;
      this.sidebar = null;
      this.overlay = null;
      this.heroEl = null;
      this.ticking = false;
      this.mobileOpen = false;
    }

    connectedCallback() {
      this.stickyHeader = this.querySelector('[data-pla-sticky-header]');
      this.sidebar = this.querySelector('[data-pla-sidebar]');
      this.heroEl = this.querySelector('.pla__hero');

      // Collect scrollspy sections and nav links
      this.sections = Array.from(this.querySelectorAll('[data-pla-section]'));
      this.navLinks = Array.from(this.querySelectorAll('[data-pla-nav-link]'));

      this.initExpandables();
      this.initSidebar();
      this.initMobileNav();
      this.initScrollSpy();
      this.initStickyHeader();
      this.initSmoothScroll();
    }

    disconnectedCallback() {
      if (this._scrollHandler) {
        window.removeEventListener('scroll', this._scrollHandler);
      }
      if (this._resizeHandler) {
        window.removeEventListener('resize', this._resizeHandler);
      }
    }

    /* ============================================================
       SCROLL SPY — highlight active section in sidebar
       ============================================================ */
    initScrollSpy() {
      this._scrollHandler = () => {
        if (!this.ticking) {
          window.requestAnimationFrame(() => {
            this.onScroll();
            this.ticking = false;
          });
          this.ticking = true;
        }
      };

      window.addEventListener('scroll', this._scrollHandler, { passive: true });
    }

    onScroll() {
      this.updateStickyHeader();
      this.updateActiveNav();
    }

    updateActiveNav() {
      const scrollY = window.scrollY;
      const headerOffset = 100;
      let activeId = null;

      // Find the section currently in view (top of viewport + offset)
      for (let i = this.sections.length - 1; i >= 0; i--) {
        const section = this.sections[i];
        const rect = section.getBoundingClientRect();
        if (rect.top <= headerOffset) {
          activeId = section.dataset.plaSection;
          break;
        }
      }

      // Default to first section if none found
      if (!activeId && this.sections.length > 0) {
        activeId = this.sections[0].dataset.plaSection;
      }

      // Update nav link states
      this.navLinks.forEach((link) => {
        const linkTarget = link.dataset.plaNavLink;
        if (linkTarget === activeId) {
          link.classList.add('is-active');
        } else {
          link.classList.remove('is-active');
        }
      });
    }

    /* ============================================================
       STICKY HEADER — show/hide based on scroll position
       ============================================================ */
    initStickyHeader() {
      // Initial check
      this.updateStickyHeader();
    }

    updateStickyHeader() {
      if (!this.stickyHeader || !this.heroEl) return;

      const heroBottom = this.heroEl.getBoundingClientRect().bottom;
      const shouldShow = heroBottom < 0;

      if (shouldShow) {
        this.stickyHeader.classList.add('is-visible');
      } else {
        this.stickyHeader.classList.remove('is-visible');
      }
    }

    /* ============================================================
       EXPAND/COLLAPSE text sections
       ============================================================ */
    initExpandables() {
      const expandables = this.querySelectorAll('[data-pla-expandable]');

      expandables.forEach((container) => {
        const toggle = container.querySelector('[data-pla-expand-toggle]');
        const content = container.querySelector('[data-pla-expand-content]');
        if (!toggle || !content) return;

        toggle.addEventListener('click', () => {
          const isOpen = toggle.getAttribute('aria-expanded') === 'true';

          if (isOpen) {
            toggle.setAttribute('aria-expanded', 'false');
            content.classList.remove('is-open');
            toggle.innerHTML = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 9l6 6 6-6"/></svg> Show Detailed Analysis`;
          } else {
            toggle.setAttribute('aria-expanded', 'true');
            content.classList.add('is-open');
            toggle.innerHTML = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 9l6 6 6-6"/></svg> Hide Analysis`;
          }
        });
      });
    }

    /* ============================================================
       SIDEBAR — sticky behavior handled by CSS
       ============================================================ */
    initSidebar() {
      // Sidebar scroll into view for active link (desktop)
      this._resizeHandler = () => {
        if (window.innerWidth >= 1024 && this.mobileOpen) {
          this.closeMobileSidebar();
        }
      };
      window.addEventListener('resize', this._resizeHandler, { passive: true });
    }

    /* ============================================================
       MOBILE NAV — drawer toggle
       ============================================================ */
    initMobileNav() {
      const toggle = this.querySelector('[data-pla-mobile-nav-toggle]');
      if (!toggle || !this.sidebar) return;

      // Create overlay
      this.overlay = document.createElement('div');
      this.overlay.className = 'pla__sidebar-overlay';
      this.appendChild(this.overlay);

      toggle.addEventListener('click', () => {
        if (this.mobileOpen) {
          this.closeMobileSidebar();
        } else {
          this.openMobileSidebar();
        }
      });

      this.overlay.addEventListener('click', () => {
        this.closeMobileSidebar();
      });

      // Close on Escape
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.mobileOpen) {
          this.closeMobileSidebar();
        }
      });

      // Close sidebar when a nav link is clicked (mobile)
      this.sidebar.querySelectorAll('.pla__nav-link, .pla__nav-sublink').forEach((link) => {
        link.addEventListener('click', () => {
          if (this.mobileOpen) {
            this.closeMobileSidebar();
          }
        });
      });
    }

    openMobileSidebar() {
      this.mobileOpen = true;
      this.sidebar.classList.add('is-open');
      this.overlay.classList.add('is-visible');
      document.body.style.overflow = 'hidden';
      const toggle = this.querySelector('[data-pla-mobile-nav-toggle]');
      if (toggle) toggle.setAttribute('aria-expanded', 'true');
    }

    closeMobileSidebar() {
      this.mobileOpen = false;
      this.sidebar.classList.remove('is-open');
      this.overlay.classList.remove('is-visible');
      document.body.style.overflow = '';
      const toggle = this.querySelector('[data-pla-mobile-nav-toggle]');
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
    }

    /* ============================================================
       SMOOTH SCROLL on sidebar link click
       ============================================================ */
    initSmoothScroll() {
      const allLinks = this.querySelectorAll('.pla__nav-link, .pla__nav-sublink');

      allLinks.forEach((link) => {
        link.addEventListener('click', (e) => {
          const href = link.getAttribute('href');
          if (!href || !href.startsWith('#')) return;

          const target = document.getElementById(href.slice(1));
          if (!target) return;

          e.preventDefault();

          const headerOffset = 80;
          const elementPosition = target.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.scrollY - headerOffset;

          // Use smooth scroll with reduced motion check
          const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

          window.scrollTo({
            top: offsetPosition,
            behavior: prefersReducedMotion ? 'auto' : 'smooth',
          });
        });
      });
    }
  }

  customElements.define('piano-lab-article', PianoLabArticle);
}
